import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddHomeWorkOutlinedIcon from "@mui/icons-material/AddHomeWorkOutlined";
import {
  type GridSelectionModel,
  type GridSortModel,
} from "@mui/x-data-grid-pro";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { useApiClient } from "@/hooks/useApiClient";
import { useIsMonthlyMember } from "@/features/brandBadge";
import { InheritanceReuseHero } from "@/features/inheritanceReuse/components/InheritanceReuseHero";
import { InheritanceReuseDisclaimer } from "@/features/inheritanceReuse/components/InheritanceReuseDisclaimer";
import { InheritanceReuseFilterPanel } from "@/features/inheritanceReuse/components/InheritanceReuseFilterPanel";
import { InheritanceReuseDataGrid } from "@/features/inheritanceReuse/components/InheritanceReuseDataGrid";
import { useGridPagination } from "@/features/inheritanceReuse/hooks/useGridPagination";
import {
  GetInheritanceReuseCandidatesAPI,
  GetInheritanceReuseCitiesAPI,
  GetInheritanceReusePrefecturesAPI,
} from "@/features/inheritanceReuse/api";
import { buildCandidatesRequest } from "@/features/inheritanceReuse/utils/buildRequest";
import { buildAcquireUrl } from "@/features/inheritanceReuse/utils/buildAcquireUrl";
import {
  buildMultipleAcquireState,
  collectSelectedEntities,
  mergeSelectedEntities,
} from "@/features/inheritanceReuse/utils/buildMultipleAcquire";
import { summarizeFilter } from "@/features/inheritanceReuse/utils/summarizeFilter";
import {
  INHERITANCE_REUSE_ACQUIRE_NOTE,
  INHERITANCE_REUSE_TITLE,
  INITIAL_FILTER_CONDITION,
} from "@/features/inheritanceReuse/utils/constants";
import type {
  IInheritanceReuseFilterCondition,
  InheritanceReuseCandidateEntity,
  InheritanceReuseCandidateRow,
} from "@/features/inheritanceReuse/types";

/**
 * 相続後リユース候補画面。
 * 「検索条件」カードと「検索結果」カードを分離。初期は検索フォーム展開、検索実行後は
 * 条件をサマリ（チップ＋「条件を変更」）へ畳み、結果一覧を主役に大きく表示する。
 * サーバサイドページング・ソート。行の「リユース取得」で所在・地番を引き継ぎ /acquirebook へ遷移。
 */
export const InheritanceReuse: React.FC = () => {
  const { apiClient } = useApiClient();
  const navigate = useNavigate();
  const theme = useTheme();
  // 月額会員限定機能。非会員（フラグONでURL直打ち到達を含む）は本体・取得導線を出さない。
  const isMonthlyMember = useIsMonthlyMember();

  // 入力中のフィルタ（ドラフト）と、検索確定済みのフィルタを分ける。
  const [draft, setDraft] = useState<IInheritanceReuseFilterCondition>(
    INITIAL_FILTER_CONDITION
  );
  const [applied, setApplied] =
    useState<IInheritanceReuseFilterCondition | null>(null);
  // 検索フォームの展開状態（初期は展開。検索実行で畳む）。
  const [searchOpen, setSearchOpen] = useState(true);

  const { page, pageSize, changePage, changePageSize, resetPage } =
    useGridPagination();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  // 行選択（複数可）。サーバサイドページングのため、選択された候補の実体をページ跨ぎで
  // 蓄積する（selectedEntities）。selectionModel は id 集合、実体マップから handoff を組む。
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [selectedEntities, setSelectedEntities] = useState<
    Record<string, InheritanceReuseCandidateEntity>
  >({});

  // 都道府県別件数（セレクトの件数表示用）。
  const {
    data: prefecturesData,
    isLoading: prefecturesLoading,
    error: prefecturesError,
    mutate: mutatePrefectures,
  } = GetInheritanceReusePrefecturesAPI(apiClient);

  // 市区町村別件数（ドラフトの都道府県依存。未選択時は発火しない）。
  const {
    data: citiesData,
    isLoading: citiesLoading,
    error: citiesError,
    mutate: mutateCities,
  } = GetInheritanceReuseCitiesAPI(
    apiClient,
    draft.prefectureName === "" ? undefined : draft.prefectureName
  );

  // 検索確定後のみリクエストを組み立てる（未確定は undefined で発火させない）。
  const request = useMemo(
    () =>
      applied === null
        ? undefined
        : buildCandidatesRequest(applied, page, pageSize, sortModel),
    [applied, page, pageSize, sortModel]
  );

  const {
    data,
    isLoading,
    error: candidatesError,
    mutate: mutateCandidates,
  } = GetInheritanceReuseCandidatesAPI(apiClient, request);

  const handleSearch = useCallback((): void => {
    setApplied(draft);
    resetPage();
    setSearchOpen(false);
    // 新しい検索では選択をクリアする。
    setSelectionModel([]);
    setSelectedEntities({});
    // 同一条件での再検索は SWR キーが変わらず再取得されないため、明示的に再検証する。
    void mutateCandidates();
  }, [draft, resetPage, mutateCandidates]);

  // 選択変更時：現在ページの実体を蓄積しつつ、選択解除された id は破棄する（ページ跨ぎ対応）。
  const handleSelectionModelChange = useCallback(
    (model: GridSelectionModel): void => {
      setSelectionModel(model);
      const currentRows = data?.results ?? [];
      setSelectedEntities((prev) =>
        mergeSelectedEntities(prev, model, currentRows)
      );
    },
    [data]
  );

  const handleBulkAcquire = useCallback((): void => {
    const rows = collectSelectedEntities(selectionModel, selectedEntities);
    if (rows.length === 0) return;
    navigate("/acquiremultiplebooks", {
      state: buildMultipleAcquireState(rows),
    });
  }, [navigate, selectionModel, selectedEntities]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel): void => {
      setSortModel(model);
      resetPage();
    },
    [resetPage]
  );

  const handleAcquire = useCallback(
    (row: InheritanceReuseCandidateRow): void => {
      navigate(buildAcquireUrl(row));
    },
    [navigate]
  );

  const retryPrefectures = useCallback((): void => {
    void mutatePrefectures();
  }, [mutatePrefectures]);

  const retryCandidates = useCallback((): void => {
    void mutateCandidates();
  }, [mutateCandidates]);

  const retryCities = useCallback((): void => {
    void mutateCities();
  }, [mutateCities]);

  const handleReset = useCallback((): void => {
    setDraft(INITIAL_FILTER_CONDITION);
  }, []);

  const summaryChips = useMemo(
    () => (applied === null ? [] : summarizeFilter(applied)),
    [applied]
  );

  const totalCount = data?.count ?? 0;

  // 会員限定ガード（FE）。BE も UserActivePlanPermission で 403 にするが、FE でも会員限定である旨を明示する。
  if (!isMonthlyMember) {
    return (
      <PagePaper>
        <InheritanceReuseHero />
        <SectionPaper>
          <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
              月額会員様限定の機能です
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.darkgray?.main }}
            >
              「{INHERITANCE_REUSE_TITLE}
              」は月額プランにご契約の会員様のみご利用いただけます。
              ご利用をご希望の場合は担当までお問い合わせください。
            </Typography>
          </Box>
        </SectionPaper>
      </PagePaper>
    );
  }

  return (
    <PagePaper>
      <InheritanceReuseHero />

      {/* 検索条件カード */}
      <Box sx={{ mb: 3 }}>
        <SectionPaper>
          <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              sx={{ mb: searchOpen ? 3 : 2 }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                検索条件
              </Typography>
              {!searchOpen && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => {
                    setSearchOpen(true);
                  }}
                >
                  条件を変更
                </Button>
              )}
            </Stack>

            {/* 都道府県件数の取得失敗（件数が出せない）。フィルタ自体は都道府県名で操作可能。 */}
            {prefecturesError != null && searchOpen && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={retryPrefectures}
                  >
                    再試行
                  </Button>
                }
              >
                都道府県別件数の取得に失敗しました。
              </Alert>
            )}

            {/* 市区町村件数の取得失敗（0件と区別してエラー＋再試行）。 */}
            {citiesError != null &&
              searchOpen &&
              draft.prefectureName !== "" && (
                <Alert
                  severity="error"
                  sx={{ mt: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={retryCities}>
                      再試行
                    </Button>
                  }
                >
                  市区町村別件数の取得に失敗しました。
                </Alert>
              )}

            {searchOpen ? (
              <InheritanceReuseFilterPanel
                value={draft}
                onChange={setDraft}
                onSearch={handleSearch}
                onReset={handleReset}
                prefectures={prefecturesData?.results ?? []}
                prefecturesLoading={prefecturesLoading}
                cities={citiesData?.results ?? []}
                citiesLoading={citiesLoading}
              />
            ) : (
              // 畳んだ状態：確定条件をチップで表示。
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {summaryChips.map((chip, index) => (
                  <Chip
                    key={`${chip}-${index}`}
                    label={chip}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: theme.palette.primary.main,
                      color:
                        theme.palette.dark?.main ?? theme.palette.primary.main,
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </SectionPaper>
      </Box>

      {/* 検索結果カード */}
      <SectionPaper>
        <Box sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1.5}
            sx={{ mb: 2 }}
            flexWrap="wrap"
          >
            <Stack direction="row" alignItems="baseline" gap={1.5}>
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                検索結果
              </Typography>
              {applied !== null && candidatesError == null && (
                <Typography
                  sx={{ fontSize: 14, color: theme.palette.darkgray?.main }}
                >
                  {totalCount.toLocaleString()} 件
                </Typography>
              )}
            </Stack>
            {/* まとめてリユース取得：選択0件で無効、選択件数を表示。行内ボタンと同系統の上質スタイル。 */}
            {applied !== null && candidatesError == null && (
              <Button
                variant="contained"
                size="small"
                disableElevation
                startIcon={<AddHomeWorkOutlinedIcon />}
                disabled={selectionModel.length === 0}
                onClick={handleBulkAcquire}
                sx={{
                  whiteSpace: "nowrap",
                  borderRadius: 1.5,
                  px: 1.75,
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "none",
                  "& .MuiButton-startIcon": { mr: 0.5 },
                  "&:hover": {
                    backgroundColor: "dark.main",
                    boxShadow: "0 2px 6px -1px rgba(3,96,121,0.4)",
                  },
                }}
              >
                まとめて登記を取得
                {selectionModel.length > 0 && `（${selectionModel.length}件）`}
              </Button>
            )}
          </Stack>

          {/* 取得できるのは所有者事項のみである旨の注記（控えめに常時表示）。 */}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 1.5,
              color: theme.palette.darkgray?.main,
            }}
          >
            {INHERITANCE_REUSE_ACQUIRE_NOTE}
          </Typography>

          {/* 推定値の精度に関する注意書き（控えめ・初期折りたたみ）。 */}
          <InheritanceReuseDisclaimer />

          {applied === null ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 6, textAlign: "center" }}
            >
              都道府県を選択して「この条件で検索」を押してください。
            </Typography>
          ) : candidatesError != null ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={retryCandidates}>
                  再試行
                </Button>
              }
            >
              候補一覧の取得に失敗しました。時間をおいて再度お試しください。
            </Alert>
          ) : (
            <Box sx={{ mt: 1 }}>
              <InheritanceReuseDataGrid
                candidates={data?.results ?? []}
                rowCount={totalCount}
                loading={isLoading}
                page={page}
                pageSize={pageSize}
                sortModel={sortModel}
                selectionModel={selectionModel}
                onPageChange={changePage}
                onPageSizeChange={changePageSize}
                onSortModelChange={handleSortModelChange}
                onSelectionModelChange={handleSelectionModelChange}
                onAcquire={handleAcquire}
              />
            </Box>
          )}
        </Box>
      </SectionPaper>
    </PagePaper>
  );
};
